import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Icon,
  Card,
  CardHeader,
  CardBody,
  Badge,
  InputGroup,
  InputRightElement,
  useDisclosure,
  Spinner,
  Table,
  Thead,
  Th,
  Box,
  Container,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Textarea,
  useToast,
  Code,
  Tag,
  Tr,
  Td,
  Stack,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  RadioGroup,
  Radio,
  Tbody,
  Box as ChakraBox,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon, SmallCloseIcon } from "@chakra-ui/icons";
import { MdOutlineTextsms } from "react-icons/md";
import { useAccountManagementData } from "./hooks/useAccountManagementData";
import { useAdminUpdatesSocket } from "./hooks/useAdminUpdatesSocket";
import { quickbooksService } from "../../api/quickbooks";
import { QuickBooksSyncModal } from "../shared/QuickBooksSyncModal";
import { ValidationResultModal } from "../shared/ValidationResultModal";

/* Custom filter icon component to work around missing FilterIcon in Chakra UI */
const FilterIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"
    />
  </Icon>
);

const CustomerRow = ({
  customer,
  isActive,
  handleCustomerClick,
  handleSyncCustomer,
  syncingCustomerId,
  handleConnectQuickbooks,
  handleDisconnectQuickbooks,
  disconnectingCustomerId,
  onNoteClick,
  onViewValidation,
  isValidationFetching = false,
}) => {
  const isSyncing = syncingCustomerId === customer.id;
  const syncEnabled =
    !isSyncing && customer.quickbooks_connected_for_user;
  const isDisconnecting = disconnectingCustomerId === customer.id;
  const isConnected = customer.quickbooks_connected_for_user;

  const handleConnect = () => {
    sessionStorage.setItem("customer_id", customer.id);
    console.log("Customer ID to connect: ", customer.id);
    handleConnectQuickbooks(customer.id);
  };

  const noteText =
    typeof customer.note === "string" ? customer.note : customer.note?.note;

  const sync = customer.sync_statuses || {};

  const hasSyncData = Boolean(
    sync?.gl_last_sync_success ||
      sync?.gl_last_sync_attempt ||
      sync?.tb_last_sync_success ||
      sync?.tb_last_sync_attempt ||
      sync?.last_validation_attempt
  );
  const validationAttempted = Boolean(sync?.last_validation_attempt);
  const latestPassed = Boolean(sync?.is_synced);

  const connectionLabel = isConnected ? "Connected" : "Unconnected";
  const connectionTagStyles = isConnected
    ? {
        bg: "green.100",
        color: "green.700",
        borderColor: "green.200",
      }
    : {
        bg: "gray.100",
        color: "gray.700",
        borderColor: "gray.200",
      };

  // Calculate sync status based on gl_end_date and validation
  const calculateSyncStatus = () => {
    if (!hasSyncData) {
      return {
        label: "Not Synced",
        styles: {
          bg: "gray.100",
          color: "gray.700",
          borderColor: "gray.200",
        },
        tooltip: "No sync history available.",
        interactive: false,
      };
    }

    const glEndDate = sync?.gl_end_date ? new Date(sync.gl_end_date) : null;
    const glLastSyncSuccess = sync?.gl_last_sync_success
      ? new Date(sync.gl_last_sync_success)
      : null;
    const glLastSyncAttempt = sync?.gl_last_sync_attempt
      ? new Date(sync.gl_last_sync_attempt)
      : null;

    // Calculate last day of last full month
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(
      lastMonth.getFullYear(),
      lastMonth.getMonth() + 1,
      0
    );

    // Check if sync failed (attempt exists but no success, or attempt is newer than success)
    const syncFailed =
      glLastSyncAttempt &&
      (!glLastSyncSuccess || glLastSyncAttempt > glLastSyncSuccess);

    if (syncFailed) {
      return {
        label: "Sync Issues",
        styles: {
          bg: "red.100",
          color: "red.700",
          borderColor: "red.200",
        },
        tooltip:
          "Latest sync attempt failed. Click to view validation report.",
        interactive: typeof onViewValidation === "function",
      };
    }

    // Check if data is outdated (more than 30 days old)
    if (glLastSyncSuccess) {
      const daysSinceSync = Math.floor(
        (today - glLastSyncSuccess) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceSync > 30) {
        return {
          label: "Outdated",
          styles: {
            bg: "red.100",
            color: "red.700",
            borderColor: "red.200",
          },
          tooltip: `Last synced ${daysSinceSync} days ago. Data is outdated.`,
          interactive: typeof onViewValidation === "function",
        };
      }
    }

    // Check if gl_end_date is current (last day of last full month or later)
    const isCurrentData = glEndDate && glEndDate >= lastDayOfLastMonth;

    // Check for validation issues
    if (validationAttempted && !latestPassed) {
      return {
        label: "Sync Issues",
        styles: {
          bg: "orange.100",
          color: "orange.700",
          borderColor: "orange.200",
        },
        tooltip:
          "Latest validation reported mismatches. Click to view validation report.",
        interactive: typeof onViewValidation === "function",
      };
    }

    // If data is current and no validation issues
    if (isCurrentData && (!validationAttempted || latestPassed)) {
      return {
        label: "Synced",
        styles: {
          bg: "green.100",
          color: "green.700",
          borderColor: "green.200",
        },
        tooltip: validationAttempted
          ? "Latest validation passed. Click to view validation report."
          : "Synced successfully. Click to view validation report.",
        interactive: typeof onViewValidation === "function",
      };
    }

    // If data is not current but within 30 days
    if (!isCurrentData && glLastSyncSuccess) {
      const daysSinceSync = Math.floor(
        (today - glLastSyncSuccess) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceSync <= 30) {
        // Green if less than 25 days, orange if between 25-29 days
        const isNearing = daysSinceSync >= 25;
        return {
          label: "Sync Needed",
          styles: isNearing
            ? {
                bg: "orange.100",
                color: "orange.700",
                borderColor: "orange.200",
              }
            : {
                bg: "green.100",
                color: "green.700",
                borderColor: "green.200",
              },
          tooltip: `Data is not current. Last synced to ${
            glEndDate ? glEndDate.toLocaleDateString() : "unknown date"
          }.${isNearing ? " Approaching 30-day limit." : ""}`,
          interactive: typeof onViewValidation === "function",
        };
      }
    }

    // Default fallback
    return {
      label: "Not Synced",
      styles: {
        bg: "gray.100",
        color: "gray.700",
        borderColor: "gray.200",
      },
      tooltip: "Sync status unknown.",
      interactive: false,
    };
  };

  const syncStatus = calculateSyncStatus();
  const syncTagLabel = syncStatus.label;
  const syncTagStyles = syncStatus.styles;
  const syncTagInteractive = syncStatus.interactive;
  let syncTagTooltip = syncStatus.tooltip;

  if (syncTagInteractive) {
    syncTagTooltip = `${syncTagTooltip}`;
  }

  const StatusPill = ({
    label,
    styles,
    interactive = false,
    onClick,
    onKeyDown,
    showSpinner = false,
  }) => (
    <ChakraBox
      as={interactive ? "button" : "span"}
      minW="108px"
      px={1.5}
      py={0.5}
      borderRadius="md"
      fontSize="sm"
      fontWeight="medium"
      textAlign="center"
      bg={styles.bg}
      color={styles.color}
      border="1px solid"
      borderColor={styles.borderColor}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      cursor={interactive ? "pointer" : "default"}
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? onKeyDown : undefined}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      transition="all 0.15s ease"
      _hover={interactive ? { boxShadow: "md" } : undefined}
      _focus={interactive ? { boxShadow: "outline" } : undefined}
    >
      {showSpinner && <Spinner size="xs" mr={2} />}
      {label}
    </ChakraBox>
  );

  const handleSyncedTagClick = () => {
    if (syncTagInteractive && !isValidationFetching) {
      onViewValidation(customer);
    }
  };

  const handleSyncedTagKeyDown = (event) => {
    if (!syncTagInteractive || isValidationFetching) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onViewValidation(customer);
    }
  };

  return (
    <Tr
      key={customer.id}
      _hover={{ bg: "gray.50" }}
      bg={isActive ? "blue.50" : undefined}
    >
      <Td>
        <Text fontWeight={isActive ? "bold" : "normal"}>{customer.name}</Text>
      </Td>
      <Td>{customer.email || ""}</Td>
      <Td>{customer.primary_contact || ""}</Td>
      <Td>
        {noteText ? (
          <Tooltip label="View/Edit Note">
            <IconButton
              aria-label="View/Edit Note"
              icon={<MdOutlineTextsms color="#ED8936" />}
              variant="ghost"
              size="sm"
              onClick={() => onNoteClick({ ...customer, note: noteText })}
            />
          </Tooltip>
        ) : (
          <Tooltip label="Add Note">
            <IconButton
              aria-label="Add Note"
              icon={<AddIcon color="#3182CE" />}
              variant="ghost"
              size="sm"
              onClick={() => onNoteClick({ ...customer, note: "" })}
            />
          </Tooltip>
        )}
      </Td>
      <Td>
        <HStack spacing={3} align="center">
          <StatusPill label={connectionLabel} styles={connectionTagStyles} />
          <Tooltip label={syncTagTooltip} hasArrow>
            <StatusPill
              label={syncTagLabel}
              styles={syncTagStyles}
              interactive={syncTagInteractive}
              onClick={handleSyncedTagClick}
              onKeyDown={handleSyncedTagKeyDown}
              showSpinner={isValidationFetching}
            />
          </Tooltip>
        </HStack>
      </Td>
      <Td>
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color="gray.700" lineHeight={1.1}>
            <b>Synced Until:</b>{" "}
            {sync.gl_end_date
              ? new Date(sync.gl_end_date).toLocaleDateString()
              : "—"}
          </Text>
          <Text fontSize="sm" color="gray.700" lineHeight={1.1}>
            <b>Last Attempt:</b>{" "}
            {(() => {
              const attempts = [
                sync.gl_last_sync_attempt,
                sync.tb_last_sync_attempt,
              ].filter(Boolean);
              if (attempts.length === 0) return "—";
              const mostRecent = attempts.reduce((latest, current) =>
                new Date(current) > new Date(latest) ? current : latest
              );
              return new Date(mostRecent).toLocaleString();
            })()}
          </Text>
        </VStack>
      </Td>
      <Td>
        {isConnected ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDisconnectQuickbooks(customer.id)}
            isLoading={isDisconnecting}
            isDisabled={isDisconnecting}
          >
            Disconnect
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={handleConnect}>
            Connect
          </Button>
        )}
      </Td>
      <Td>
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          isDisabled={!syncEnabled}
          isLoading={isSyncing && syncingCustomerId === customer.id}
          onClick={() => handleSyncCustomer(customer.id, "both")}
          title={
            isConnected ? "Sync QuickBooks Data" : "Connect to QuickBooks first"
          }
        >
          Sync
        </Button>
      </Td>
      <Td>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleCustomerClick(customer.id)}
        >
          View
        </Button>
      </Td>
    </Tr>
  );
};

const NoteModal = ({ isOpen, onClose, note, onSave, readOnly }) => {
  const [editValue, setEditValue] = useState(
    typeof note === "string" ? note : note?.note || ""
  );
  const textareaRef = useRef();
  useEffect(() => {
    setEditValue(typeof note === "string" ? note : note?.note || "");
  }, [note]);
  useEffect(() => {
    if (!readOnly && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const maxHeight = window.innerHeight * 0.5; // 50vh
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, maxHeight) + "px";
    }
  }, [editValue, isOpen, readOnly]);
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="600px" maxH="70vh">
        <ModalHeader>Note</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Note</FormLabel>
            <Textarea
              ref={textareaRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus={!readOnly}
              placeholder="Enter note..."
              mb={2}
              width="100%"
              isReadOnly={readOnly}
              {...(readOnly
                ? {
                    height: "50vh",
                    overflowY: "auto",
                    fontSize: "lg",
                    color: "gray.800",
                  }
                : { rows: 3, minH: "90px", maxH: "50vh", resize: "vertical" })}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          {readOnly ? (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} mr={3}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={() => onSave(editValue)}>
                Save
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ImportConfirmationModal = ({
  isOpen,
  onClose,
  customer,
  onConfirm,
  isLoading,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text color="red.600">⚠️ Import Raw QuickBooks Data</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              You are about to import raw QuickBooks data for{" "}
              <strong>{customer?.name}</strong> into Journal Entries.
            </Text>

            <Box
              bg="red.50"
              p={4}
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="red.400"
            >
              <Heading size="sm" color="red.700" mb={2}>
                ⚠️ DESTRUCTIVE OPERATION
              </Heading>
              <VStack align="start" spacing={2}>
                <Text color="red.700" fontSize="sm">
                  • <strong>All existing Journal Entries</strong> for this
                  customer will be permanently deleted
                </Text>
                <Text color="red.700" fontSize="sm">
                  • <strong>Account balances</strong> will be recalculated from
                  the imported data
                </Text>
                <Text color="red.700" fontSize="sm">
                  • This action <strong>cannot be undone</strong>
                </Text>
              </VStack>
            </Box>

            <Box
              bg="blue.50"
              p={4}
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="blue.400"
            >
              <Heading size="sm" color="blue.700" mb={2}>
                What will happen:
              </Heading>
              <VStack align="start" spacing={1}>
                <Text color="blue.700" fontSize="sm">
                  1. Delete all existing Journal Entries for this customer
                </Text>
                <Text color="blue.700" fontSize="sm">
                  2. Process raw QuickBooks data with account mapping
                </Text>
                <Text color="blue.700" fontSize="sm">
                  3. Create new Journal Entries from processed data
                </Text>
                <Text color="blue.700" fontSize="sm">
                  4. Recalculate all account balances
                </Text>
              </VStack>
            </Box>

            <Text fontWeight="bold" color="gray.700">
              Are you sure you want to proceed?
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            mr={3}
            onClick={onClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={onConfirm}
            isLoading={isLoading}
            loadingText="Importing..."
          >
            Yes, Import Data
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const DisconnectConfirmationModal = ({ isOpen, onClose, customer, onConfirm }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text color="orange.600">⚠️ Disconnect QuickBooks</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              You are about to disconnect QuickBooks for{" "}
              <strong>{customer?.name}</strong>.
            </Text>

            <Box
              bg="orange.50"
              p={4}
              borderRadius="md"
              borderLeft="4px solid"
              borderLeftColor="orange.400"
            >
              <Heading size="sm" color="orange.800" mb={2}>
                This will:
              </Heading>
              <VStack align="start" spacing={1}>
                <Text color="orange.700" fontSize="sm">
                  • Remove the QuickBooks connection for this customer
                </Text>
                <Text color="orange.700" fontSize="sm">
                  • Stop any scheduled syncs for this customer
                </Text>
                <Text color="orange.700" fontSize="sm">
                  • Prevent future data syncing until reconnected
                </Text>
              </VStack>
            </Box>

            <Text fontWeight="bold" color="gray.700">
              Are you sure you want to disconnect QuickBooks?
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            mr={3}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            onClick={onConfirm}
          >
            Yes, Disconnect
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Add a helper to save page state to sessionStorage
function saveAccountManagementPageState({ search, filters, cursor }) {
  const state = {
    search,
    filters,
    cursor: cursor || null,
  };
  sessionStorage.setItem("account_management_page_state", JSON.stringify(state));
}

function loadAccountManagementPageState() {
  const state = sessionStorage.getItem("account_management_page_state");
  if (!state) return null;
  try {
    return JSON.parse(state);
  } catch {
    return null;
  }
}

const defaultLastSyncFilter = {
  mode: "success",
  type: "any",
  date: "",
  dateDirection: "before",
};

const AccountManagement = () => {
  const navigate = useNavigate();
  const {
    customers,
    loading,
    nextCursor,
    previousCursor,
    syncingCustomerId,
    fullSyncingCustomerId,
    activeJobs,
    handleNextPage,
    handlePreviousPage,
    handleSyncCustomer,
    handleFullSyncCustomer,
    updateCustomerInList,
    handleJobUpdate,
    searchCustomers,
    isSyncModalOpen,
    closeSyncModal,
    selectedCustomerForSync,
    performCustomerSync,
    handleConnectQuickbooks,
    handleDisconnectQuickbooks,
    isDisconnectModalOpen,
    closeDisconnectModal,
    selectedCustomerForDisconnect,
    confirmDisconnectQuickbooks,
    saveCustomerNote,
  } = useAccountManagementData();

  const { customerUpdates, jobUpdates } = useAdminUpdatesSocket();

  const [search, setSearch] = useState("");
  const toast = useToast();

  // Process WebSocket updates to show completion notifications
  useEffect(() => {
    if (customerUpdates.length > 0) {
      const latestUpdate = customerUpdates[customerUpdates.length - 1];
      const { payload } = latestUpdate;

      if (payload && payload.customer_id) {
        // Update the customer in the list to refresh sync status
        updateCustomerInList({
          id: payload.customer_id,
          sync_statuses: payload.sync_statuses || {},
        });

        // Show completion toast notification (Note: this may be redundant with polling notifications)
        if (payload.status === "completed") {
          const customer = customers.find((c) => c.id === payload.customer_id);
          const customerName = customer
            ? customer.name
            : `Customer ${payload.customer_id}`;

          console.log("WebSocket sync completion received for:", customerName);
        } else if (payload.status === "failed") {
          const customer = customers.find((c) => c.id === payload.customer_id);
          const customerName = customer
            ? customer.name
            : `Customer ${payload.customer_id}`;

          console.log("WebSocket sync failure received for:", customerName);
        }
      }
    }
  }, [customerUpdates, updateCustomerInList, customers]);

  /* Store filter state at component level instead of in the hook to maintain separation of concerns
     and allow the hook to be reused in other contexts */
  const [filters, setFilters] = useState({
    connectionStatus: "all", // 'all', 'connected', 'not_connected'
    lastSyncFilter: {
      mode: "success", // 'success' or 'failed'
      type: "any", // 'any', 'gl', or 'tb'
      date: "", // ISO string
      dateDirection: "before", // 'before' or 'after'
    }, // { mode, type, date, dateDirection }
  });

  /* Track count for conditional UI rendering, avoiding unnecessary filter indicator section */
  const activeFiltersCount =
    (filters.connectionStatus !== "all" ? 1 : 0) +
    (filters.lastSyncFilter ? 1 : 0);

  const [disconnectingCustomerId, setDisconnectingCustomerId] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteCustomer, setNoteCustomer] = useState(null);
  const [syncNoteModalOpen, setSyncNoteModalOpen] = useState(false);
  const [syncNoteText, setSyncNoteText] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importCustomer, setImportCustomer] = useState(null);
  const [importing, setImporting] = useState(false);
  const {
    isOpen: isValidationModalOpen,
    onOpen: openValidationModal,
    onClose: closeValidationModal,
  } = useDisclosure();
  const [validationCustomer, setValidationCustomer] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [validationLoadingCustomerId, setValidationLoadingCustomerId] =
    useState(null);
  const validationPollTimeout = useRef(null);

  const isValidationPending = useCallback((syncStatus) => {
    if (!syncStatus) return false;
    const lastSyncSuccess =
      syncStatus.gl_last_sync_success || syncStatus.gl_last_sync_attempt;
    if (!lastSyncSuccess) {
      return false;
    }
    const validationAttempt = syncStatus.last_validation_attempt;
    if (!validationAttempt) {
      return true;
    }
    return new Date(validationAttempt) < new Date(lastSyncSuccess);
  }, []);

  const clearValidationPoll = useCallback(() => {
    if (validationPollTimeout.current) {
      clearTimeout(validationPollTimeout.current);
      validationPollTimeout.current = null;
    }
  }, []);

  const scheduleValidationRefresh = useCallback(
    (customerId) => {
      clearValidationPoll();
      validationPollTimeout.current = setTimeout(async () => {
        try {
          const response = await quickbooksService.getCustomerById(customerId, {
            include_quickbooks_sync_info: true,
            include_quickbooks_sync_results: true,
            include_quickbooks_connection: true,
          });

          const refreshed = response.data;
          setValidationCustomer(refreshed);
          setValidationResults(
            refreshed?.sync_statuses?.last_validation_results || null
          );

          if (isValidationPending(refreshed?.sync_statuses)) {
            scheduleValidationRefresh(customerId);
          } else {
            setValidationLoadingCustomerId(null);
            clearValidationPoll();
          }
        } catch (error) {
          console.error("Error refreshing validation results:", error);
          setValidationLoadingCustomerId(null);
          clearValidationPoll();
        }
      }, 4000);
    },
    [clearValidationPoll, isValidationPending]
  );

  // Restore state on mount if present
  useEffect(() => {
    const restored = loadAccountManagementPageState();
    if (restored) {
      setSearch(restored.search || "");
      setFilters(
        restored.filters || { connectionStatus: "all", lastSyncFilter: null }
      );
      // Optionally, restore pagination cursor if you want to restore the exact page
      if (restored.cursor) {
        // You may want to call a function to fetch data for this cursor
        // For now, just call applyFiltersAndSearch with restored state
        applyFiltersAndSearch(restored.search || "", restored.filters || {});
      } else {
        applyFiltersAndSearch(restored.search || "", restored.filters || {});
      }
      sessionStorage.removeItem("account_management_page_state");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearValidationPoll(), [clearValidationPoll]);

  const handleCustomerClick = useCallback(
    (customerId) => {
      saveAccountManagementPageState({ search, filters, cursor: null });
      window.location.href = `/client/${customerId}`;
    },
    [search, filters]
  );

  const handleViewRawData = useCallback(
    (customerId) => {
      navigate(`/quickbooks-raw-data/${customerId}`);
    },
    [navigate]
  );

  const handleImportRawData = useCallback(
    (customerId) => {
      const customer = customers.find((c) => c.id === customerId);
      setImportCustomer(customer);
      setImportModalOpen(true);
    },
    [customers]
  );

  const handleCloseValidationModal = useCallback(() => {
    closeValidationModal();
    setValidationCustomer(null);
    setValidationResults(null);
    setValidationLoadingCustomerId(null);
    clearValidationPoll();
  }, [closeValidationModal, clearValidationPoll]);

  const handleViewValidation = useCallback(
    async (customer) => {
      if (!customer?.id) {
        return;
      }

      setValidationCustomer(customer);
      setValidationResults(null);
      setValidationLoadingCustomerId(customer.id);
      openValidationModal();

      try {
        const response = await quickbooksService.getCustomerById(customer.id, {
          include_quickbooks_sync_info: true,
          include_quickbooks_sync_results: true,
          include_quickbooks_connection: true,
        });

        const data = response.data;
        setValidationCustomer(data);
        setValidationResults(
          data?.sync_statuses?.last_validation_results || null
        );

        if (isValidationPending(data?.sync_statuses)) {
          scheduleValidationRefresh(customer.id);
        } else {
          setValidationLoadingCustomerId(null);
        }
      } catch (error) {
        console.error("Error loading validation results:", error);
        toast({
          title: "Validation Results",
          description: "Unable to load validation report.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
        handleCloseValidationModal();
      } finally {
        if (!validationPollTimeout.current) {
          setValidationLoadingCustomerId(null);
        }
      }
    },
    [
      openValidationModal,
      toast,
      handleCloseValidationModal,
      isValidationPending,
      scheduleValidationRefresh,
      clearValidationPoll,
    ]
  );

  const confirmImportRawData = useCallback(async () => {
    if (!importCustomer) return;

    setImporting(true);
    try {
      const response = await quickbooksService.syncQuickbooksData({
        syncType: 'import',
        fetchMode: 'none',
        importMode: 'import',
        customer_ids: [importCustomer.id]
      });

      // Handle async response (HTTP 202)
      if (response.status === 202 && response.data.task_id) {
        toast({
          title: "Import Task Started",
          description: `Redirecting to job details page to monitor progress...`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        setImportModalOpen(false);

        // Redirect to the sync job details page to view this workflow using React Router
        navigate(`/sync-jobs/${response.data.task_id}`);
        return;
      } else {
        // Fallback for immediate response format (if any endpoints still use it)
        const message =
          response.data.message || "Raw data import completed successfully";
        toast({
          title: "Import Completed",
          description: message,
          status: "success",
          duration: 8000,
          isClosable: true,
        });
        setImportModalOpen(false);
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        "Failed to start import task";

      toast({
        title: "Import Failed",
        description: errorMessage,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setImporting(false);
    }
  }, [importCustomer, toast, navigate]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));

    /* Apply immediately for instant feedback without requiring an extra button click */
    applyFiltersAndSearch(search, {
      ...filters,
      [filterType]: value,
    });
  };

  const clearFilters = () => {
    setFilters({
      connectionStatus: "all",
      lastSyncFilter: defaultLastSyncFilter,
    });

    /* Preserve current search when clearing filters */
    searchCustomers(search);
  };

  /* Combine search and filter operations to prevent multiple redundant API calls */
  const applyFiltersAndSearch = useCallback(
    (searchText, currentFilters = filters) => {
      const params = new URLSearchParams();

      if (searchText) {
        params.append("search", searchText);
      }

      const filterParams = {};

      // Use quickbooksConnectionStatus with three possible values
      if (currentFilters.connectionStatus === "connected") {
        filterParams.quickbooksConnectionStatus = "connected";
      } else if (currentFilters.connectionStatus === "not_connected") {
        filterParams.quickbooksConnectionStatus = "unconnected";
      } else {
        filterParams.quickbooksConnectionStatus = "all";
      }

      if (currentFilters.lastSyncFilter) {
        params.append("last_sync_before", currentFilters.lastSyncFilter.date);
        filterParams.last_sync_before = currentFilters.lastSyncFilter.date;
      }

      const paramsString = params.toString();
      console.log("Applying filters with params:", paramsString);
      console.log("Filter state:", {
        connectionStatus: currentFilters.connectionStatus,
        lastSyncFilter: currentFilters.lastSyncFilter,
        filterParams,
      });

      searchCustomers(searchText, paramsString, filterParams);
    },
    [searchCustomers, filters]
  );

  const handleSearchChange = useCallback(
    (e) => {
      const newSearchText = e.target.value;
      setSearch(newSearchText);
      /* Search as-you-type for better UX, matching behavior in other app components */
      applyFiltersAndSearch(newSearchText);
    },
    [applyFiltersAndSearch]
  );

  const handleDisconnectQuickbooksWrapper = async (customerId) => {
    saveAccountManagementPageState({ search, filters, cursor: null });
    setDisconnectingCustomerId(customerId);
    try {
      await handleDisconnectQuickbooks(customerId);
    } finally {
      setDisconnectingCustomerId(null);
    }
  };

  const handleNoteClick = (customer) => {
    setNoteCustomer(customer);
    setNoteModalOpen(true);
  };

  const handleNoteSave = async (newNote) => {
    if (!noteCustomer) return;
    await saveCustomerNote(noteCustomer.id, newNote);
    setNoteModalOpen(false);
  };

  // Handler to open the sync note modal from CustomerSyncModal
  const handleShowSyncNote = (noteText) => {
    setSyncNoteText(noteText);
    setSyncNoteModalOpen(true);
  };

  useEffect(() => {
    if (customerUpdates.length > 0) {
      const latestUpdate = customerUpdates[customerUpdates.length - 1];

      if (latestUpdate?.payload?.customer_id) {
        const customerId = latestUpdate.payload.customer_id;
        const syncInfo = latestUpdate.payload.sync_info || {};

        // Check if this is a false failure notification immediately after starting a sync
        const syncStartTime = sessionStorage.getItem(
          `sync_started_${customerId}`
        );
        const currentTime = new Date().getTime();
        const syncStartTimeDate = syncStartTime
          ? new Date(syncStartTime).getTime()
          : 0;
        const timeSinceSyncStarted = currentTime - syncStartTimeDate;

        // If this is a failure notification within 5 seconds of starting a sync, ignore it
        // It's likely a race condition or premature failure notification
        if (
          syncInfo.status !== "success" &&
          syncStartTime &&
          timeSinceSyncStarted < 5000
        ) {
          console.log(
            "Ignoring likely false failure notification received shortly after sync start"
          );
          return;
        }

        // Clear the sync start time after processing
        if (syncStartTime) {
          sessionStorage.removeItem(`sync_started_${customerId}`);
        }

        // Fetch the updated customer data from the server to get fresh sync_statuses
        quickbooksService
          .getCustomerById(customerId, { include_quickbooks_sync_info: true })
          .then((response) => {
            if (response.data) {
              updateCustomerInList(response.data);
            }
          })
          .catch((error) => {
            console.error("Error fetching updated customer data:", error);
            // Fallback to basic update if fetch fails
            const updatedCustomer = {
              id: customerId,
              quickbooks_last_sync: syncInfo.timestamp || new Date().toISOString(),
              quickbooks_sync_status: syncInfo.status || "success",
            };
            updateCustomerInList(updatedCustomer);
          });

        toast({
          title:
            syncInfo.status === "success" ? "Sync Completed" : "Sync Failed",
          description: `Customer ID ${customerId} sync ${
            syncInfo.status === "success" ? "completed successfully" : "failed"
          }.`,
          status: syncInfo.status === "success" ? "success" : "error",
          duration: 10000,
          isClosable: true,
        });
      }
    }
  }, [customerUpdates, updateCustomerInList, toast]);

  useEffect(() => {
    if (jobUpdates.length > 0) {
      const latestUpdate = jobUpdates[jobUpdates.length - 1];

      if (latestUpdate?.payload?.job_id) {
        const jobId = latestUpdate.payload.job_id;
        const isStarted = latestUpdate.type === "job.started";

        handleJobUpdate(
          {
            id: jobId,
            ...latestUpdate.payload.job_details,
          },
          isStarted ? "start" : "finish"
        );

        if (!isStarted) {
          toast({
            title: "Sync Job Completed",
            description: `Job ${jobId} has finished processing.`,
            status: "info",
            duration: 10000,
            isClosable: true,
          });
        }
      }
    }
  }, [jobUpdates, handleJobUpdate, toast]);

  useEffect(() => {
    sessionStorage.removeItem("qb_connect_origin");
    sessionStorage.removeItem("qb_connect_page_params");
  }, []);

  return (
    <Container maxW="100%" pt={"80px"}>
      <Stack py={8}>
        <NoteModal
          isOpen={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          note={noteCustomer?.note}
          onSave={handleNoteSave}
        />
        <NoteModal
          isOpen={syncNoteModalOpen}
          onClose={() => setSyncNoteModalOpen(false)}
          note={syncNoteText}
          readOnly
        />
        <QuickBooksSyncModal
          isOpen={isSyncModalOpen}
          onClose={closeSyncModal}
          customer={selectedCustomerForSync}
          onSyncConfirmed={performCustomerSync}
          onShowFullNote={handleShowSyncNote}
          isAdmin={true}
          isLoading={syncingCustomerId === selectedCustomerForSync?.id}
        />
        <ImportConfirmationModal
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          customer={importCustomer}
          onConfirm={confirmImportRawData}
          isLoading={importing}
        />
        <ValidationResultModal
          isOpen={isValidationModalOpen}
          onClose={handleCloseValidationModal}
          customer={validationCustomer}
          results={validationResults}
          isLoading={Boolean(validationLoadingCustomerId)}
        />
        <DisconnectConfirmationModal
          isOpen={isDisconnectModalOpen}
          onClose={closeDisconnectModal}
          customer={selectedCustomerForDisconnect}
          onConfirm={confirmDisconnectQuickbooks}
        />

        <Card>
          <CardHeader>
            <Flex
              justify="space-between"
              align="center"
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <Box>
                <Heading size="lg">Account Management</Heading>
                <Text mt={2} color="gray.600">
                  Manage customer accounts and QuickBooks connections
                </Text>
              </Box>

              <Flex
                direction={{ base: "column", lg: "row" }}
                gap={4}
                alignItems={{ base: "stretch", lg: "center" }}
              >
                <HStack
                  spacing={3}
                  flex={{ lg: "1" }}
                  justifyContent={{ lg: "flex-end" }}
                >
                  <Button
                    as={RouterLink}
                    to="/bulk-quickbooks-sync"
                    colorScheme="blue"
                    leftIcon={<AddIcon />}
                    size="md"
                  >
                    Bulk QuickBooks Sync
                  </Button>

                  <Button
                    as={RouterLink}
                    to="/quickbooks-sync-history"
                    colorScheme="teal"
                    variant="outline"
                    title="View all QuickBooks sync jobs"
                  >
                    Sync History
                  </Button>

                  <Button
                    as={RouterLink}
                    to="/quickbooks-sync-history?status=active"
                    colorScheme="green"
                    variant="outline"
                    size="md"
                    isDisabled={!activeJobs || activeJobs.length === 0}
                    title={
                      activeJobs && activeJobs.length > 0
                        ? "View currently running sync jobs"
                        : "No active sync jobs"
                    }
                  >
                    Active Jobs{" "}
                    {activeJobs && activeJobs.length > 0
                      ? `(${activeJobs.length})`
                      : ""}
                  </Button>
                </HStack>

                <InputGroup maxW={{ lg: "300px" }}>
                  <Input
                    placeholder="Search customers..."
                    value={search}
                    onChange={handleSearchChange}
                    aria-label="Search customers"
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      icon={<SearchIcon />}
                      onClick={() => applyFiltersAndSearch(search)}
                      aria-label="Search"
                    />
                  </InputRightElement>
                </InputGroup>
              </Flex>
            </Flex>
          </CardHeader>

          <CardBody>
            <Stack spacing={6}>
              {activeFiltersCount > 0 && (
                <Flex justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Text fontWeight="bold">Active Filters:</Text>

                    {filters.connectionStatus !== "all" && (
                      <Badge
                        colorScheme="blue"
                        borderRadius="full"
                        px={2}
                        py={1}
                      >
                        Status:{" "}
                        {filters.connectionStatus === "connected"
                          ? "Connected"
                          : "Unconnected"}
                        <IconButton
                          size="xs"
                          ml={1}
                          icon={<SmallCloseIcon />}
                          onClick={() =>
                            handleFilterChange("connectionStatus", "all")
                          }
                          aria-label="Clear connection status filter"
                          variant="ghost"
                        />
                      </Badge>
                    )}

                    {filters.lastSyncFilter && filters.lastSyncFilter.date && (
                      <Tooltip
                        label={`Filtering by last ${
                          filters.lastSyncFilter.mode === "success"
                            ? "successful"
                            : "failed"
                        } sync (${
                          filters.lastSyncFilter.type === "any"
                            ? "any"
                            : filters.lastSyncFilter.type.toUpperCase()
                        }) ${filters.lastSyncFilter.dateDirection} ${new Date(
                          filters.lastSyncFilter.date
                        ).toLocaleDateString()}`}
                      >
                        <Badge
                          colorScheme="purple"
                          borderRadius="full"
                          px={2}
                          py={1}
                        >
                          Last Sync:{" "}
                          {filters.lastSyncFilter.mode === "success"
                            ? "Success"
                            : "Failed"}
                          ,{" "}
                          {filters.lastSyncFilter.type === "any"
                            ? "Any"
                            : filters.lastSyncFilter.type.toUpperCase()}
                          ,{" "}
                          {filters.lastSyncFilter.dateDirection
                            .charAt(0)
                            .toUpperCase() +
                            filters.lastSyncFilter.dateDirection.slice(1)}{" "}
                          {new Date(
                            filters.lastSyncFilter.date
                          ).toLocaleDateString()}
                          <IconButton
                            size="xs"
                            ml={1}
                            icon={<SmallCloseIcon />}
                            onClick={() =>
                              setFilters((f) => ({
                                ...f,
                                lastSyncFilter: null,
                              }))
                            }
                            aria-label="Clear last sync filter"
                            variant="ghost"
                          />
                        </Badge>
                      </Tooltip>
                    )}
                  </HStack>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearFilters}
                    leftIcon={<SmallCloseIcon />}
                  >
                    Clear All Filters
                  </Button>
                </Flex>
              )}

              <Box overflowX="auto">
                <CustomerTable
                  customers={customers}
                  isActive={syncingCustomerId}
                  handleCustomerClick={handleCustomerClick}
                  handleSyncCustomer={handleSyncCustomer}
                  syncingCustomerId={syncingCustomerId}
                  handleConnectQuickbooks={handleConnectQuickbooks}
                  handleDisconnectQuickbooks={handleDisconnectQuickbooksWrapper}
                  disconnectingCustomerId={disconnectingCustomerId}
                  loading={loading}
                  filters={filters}
                  setFilters={setFilters}
                  handleFilterChange={handleFilterChange}
                  onNoteClick={handleNoteClick}
                  onViewValidation={handleViewValidation}
                  validationLoadingCustomerId={validationLoadingCustomerId}
                />
              </Box>

              {(nextCursor || previousCursor) && (
                <Flex justify="center" mt={4}>
                  <Button
                    onClick={() => {
                      console.log(
                        "Previous button clicked, cursor:",
                        previousCursor
                      );
                      handlePreviousPage();
                    }}
                    isDisabled={!previousCursor || loading}
                    mr={2}
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => {
                      console.log("Next button clicked, cursor:", nextCursor);
                      handleNextPage();
                    }}
                    isDisabled={!nextCursor || loading}
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </Stack>
          </CardBody>
        </Card>
      </Stack>
    </Container>
  );
};

const CustomerTable = ({
  customers,
  isActive,
  handleCustomerClick,
  handleSyncCustomer,
  syncingCustomerId,
  handleConnectQuickbooks,
  handleDisconnectQuickbooks,
  disconnectingCustomerId,
  loading,
  filters,
  setFilters,
  handleFilterChange,
  onNoteClick,
  onViewValidation,
  validationLoadingCustomerId,
}) => {
  const statusDisclosure = useDisclosure();
  const dateDisclosure = useDisclosure();
  const initialFocusRef = useRef();

  // In the Last Sync filter popover, use local state for editing:
  const [localLastSyncFilter, setLocalLastSyncFilter] = useState(
    filters.lastSyncFilter
  );

  // Reset localLastSyncFilter when the popover opens
  useEffect(() => {
    if (dateDisclosure.isOpen) {
      setLocalLastSyncFilter(filters.lastSyncFilter || defaultLastSyncFilter);
    }
  }, [dateDisclosure.isOpen, filters.lastSyncFilter]);

  if (loading) {
    return (
      <Box p="8" textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">
          Loading customers...
        </Text>
      </Box>
    );
  }

  if (customers.length === 0) {
    return (
      <Box p="8" textAlign="center">
        <Text fontSize="xl" color="gray.600">
          No customers found
        </Text>
      </Box>
    );
  }

  const getRelevantSyncDate = (customer, filter) => {
    const sync = customer.sync_statuses || {};
    let glSuccess = sync.gl_last_sync_success
      ? new Date(sync.gl_last_sync_success)
      : null;
    let tbSuccess = sync.tb_last_sync_success
      ? new Date(sync.tb_last_sync_success)
      : null;
    let glAttempt = sync.gl_last_sync_attempt
      ? new Date(sync.gl_last_sync_attempt)
      : null;
    let tbAttempt = sync.tb_last_sync_attempt
      ? new Date(sync.tb_last_sync_attempt)
      : null;

    if (filter.mode === "success") {
      if (filter.type === "gl") return glSuccess;
      if (filter.type === "tb") return tbSuccess;
      // any: use max of both
      if (glSuccess && tbSuccess)
        return glSuccess > tbSuccess ? glSuccess : tbSuccess;
      return glSuccess || tbSuccess;
    } else if (filter.mode === "failed") {
      // failed = last attempt != last success
      let glFailed =
        glAttempt && (!glSuccess || glAttempt.getTime() !== glSuccess.getTime())
          ? glAttempt
          : null;
      let tbFailed =
        tbAttempt && (!tbSuccess || tbAttempt.getTime() !== tbSuccess.getTime())
          ? tbAttempt
          : null;
      if (filter.type === "gl") return glFailed;
      if (filter.type === "tb") return tbFailed;
      // any: use max of both
      if (glFailed && tbFailed)
        return glFailed > tbFailed ? glFailed : tbFailed;
      return glFailed || tbFailed;
    }
    return null;
  };

  const filteredCustomers = customers.filter((customer) => {
    const filter = filters.lastSyncFilter;
    if (!filter || !filter.date) return true;
    const relevantDate = getRelevantSyncDate(customer, filter);
    if (!relevantDate) return false;
    const filterDate = new Date(filter.date);
    if (filter.dateDirection === "before") {
      return relevantDate < filterDate;
    } else {
      return relevantDate > filterDate;
    }
  });

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>Name</Th>
          <Th>Email</Th>
          <Th>Primary Contact</Th>
          <Th>Note</Th>
          <Th>
            <Flex align="center">
              <Text mr={2}>Status</Text>
              <Popover
                initialFocusRef={initialFocusRef}
                placement="bottom-start"
                closeOnBlur={true}
                isOpen={statusDisclosure.isOpen}
                onOpen={statusDisclosure.onOpen}
                onClose={statusDisclosure.onClose}
              >
                <PopoverTrigger>
                  <IconButton
                    aria-label="Filter by connection status"
                    icon={<FilterIcon />}
                    variant="ghost"
                    size="sm"
                    colorScheme={
                      filters.connectionStatus !== "all" ? "blue" : "gray"
                    }
                  />
                </PopoverTrigger>
                <PopoverContent width="250px">
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader fontWeight="bold">
                    Filter by Status
                  </PopoverHeader>
                  <PopoverBody>
                    <RadioGroup
                      onChange={(value) => {
                        handleFilterChange("connectionStatus", value);
                        statusDisclosure.onClose();
                      }}
                      value={filters.connectionStatus}
                    >
                      <Stack spacing={2}>
                        <Radio value="all" ref={initialFocusRef}>
                          All
                        </Radio>
                        <Radio value="connected">Connected</Radio>
                        <Radio value="not_connected">Unconnected</Radio>
                      </Stack>
                    </RadioGroup>
                  </PopoverBody>
                  <PopoverFooter display="flex" justifyContent="flex-end">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        handleFilterChange("connectionStatus", "all");
                        statusDisclosure.onClose();
                      }}
                    >
                      Clear
                    </Button>
                  </PopoverFooter>
                </PopoverContent>
              </Popover>
            </Flex>
          </Th>
          <Th>
            <Flex align="center">
              <Text mr={2}>Synced Until / Last Attempt</Text>
              <Popover
                initialFocusRef={initialFocusRef}
                placement="bottom-start"
                closeOnBlur={true}
                isOpen={dateDisclosure.isOpen}
                onOpen={dateDisclosure.onOpen}
                onClose={dateDisclosure.onClose}
              >
                <PopoverTrigger>
                  <IconButton
                    aria-label="Filter by last sync date"
                    icon={<FilterIcon />}
                    variant="ghost"
                    size="sm"
                    colorScheme={filters.lastSyncFilter ? "blue" : "gray"}
                  />
                </PopoverTrigger>
                <PopoverContent width="320px">
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader fontWeight="bold">
                    Filter by Last Sync
                  </PopoverHeader>
                  <PopoverBody>
                    <FormControl mb={2}>
                      <FormLabel fontSize="sm">Date</FormLabel>
                      <Flex align="center" gap={2}>
                        <RadioGroup
                          value={localLastSyncFilter.dateDirection}
                          onChange={(val) =>
                            setLocalLastSyncFilter((f) => ({
                              ...f,
                              dateDirection: val,
                            }))
                          }
                        >
                          <Stack direction="row">
                            <Radio value="before">Before</Radio>
                            <Radio value="after">After</Radio>
                          </Stack>
                        </RadioGroup>
                        <Input
                          type="date"
                          value={
                            localLastSyncFilter.date
                              ? localLastSyncFilter.date.substring(0, 10)
                              : ""
                          }
                          onChange={(e) =>
                            setLocalLastSyncFilter((f) => ({
                              ...f,
                              date: e.target.value
                                ? new Date(e.target.value).toISOString()
                                : "",
                            }))
                          }
                          size="sm"
                        />
                      </Flex>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Show customers whose last{" "}
                        <b>
                          {localLastSyncFilter.mode === "success"
                            ? "successful"
                            : "failed"}
                        </b>{" "}
                        sync (
                        <b>
                          {localLastSyncFilter.type === "any"
                            ? "any"
                            : localLastSyncFilter.type.toUpperCase()}
                        </b>
                        ) is <b>{localLastSyncFilter.dateDirection}</b> this
                        date.
                      </Text>
                    </FormControl>
                    <FormControl mb={2}>
                      <FormLabel fontSize="sm">Mode</FormLabel>
                      <RadioGroup
                        value={localLastSyncFilter.mode}
                        onChange={(val) =>
                          setLocalLastSyncFilter((f) => ({ ...f, mode: val }))
                        }
                      >
                        <Stack direction="row">
                          <Radio value="success">Success</Radio>
                          <Radio value="failed">Failed</Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Type</FormLabel>
                      <RadioGroup
                        value={localLastSyncFilter.type}
                        onChange={(val) =>
                          setLocalLastSyncFilter((f) => ({ ...f, type: val }))
                        }
                      >
                        <Stack direction="row">
                          <Radio value="any">Any</Radio>
                          <Radio value="gl">GL</Radio>
                          <Radio value="tb">TB</Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                  </PopoverBody>
                  <PopoverFooter display="flex" justifyContent="flex-end">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        setFilters((f) => ({
                          ...f,
                          lastSyncFilter: { ...localLastSyncFilter },
                        }));
                        dateDisclosure.onClose();
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      size="sm"
                      ml={2}
                      onClick={() => {
                        setLocalLastSyncFilter({
                          mode: "success",
                          type: "any",
                          date: "",
                          dateDirection: "before",
                        });
                        setFilters((f) => ({
                          ...f,
                          lastSyncFilter: {
                            mode: "success",
                            type: "any",
                            date: "",
                            dateDirection: "before",
                          },
                        }));
                        dateDisclosure.onClose();
                      }}
                    >
                      Clear
                    </Button>
                  </PopoverFooter>
                </PopoverContent>
              </Popover>
            </Flex>
          </Th>
          <Th>Connection</Th>
          <Th>Sync</Th>
          <Th>Details</Th>
        </Tr>
      </Thead>
      <Tbody>
        {filteredCustomers.map((customer) => {
          return (
            <CustomerRow
              key={customer.id}
              customer={customer}
              isActive={isActive === customer.id}
              handleCustomerClick={handleCustomerClick}
              handleSyncCustomer={handleSyncCustomer}
              syncingCustomerId={syncingCustomerId}
              handleConnectQuickbooks={handleConnectQuickbooks}
              handleDisconnectQuickbooks={handleDisconnectQuickbooks}
              disconnectingCustomerId={disconnectingCustomerId}
              onNoteClick={onNoteClick}
              onViewValidation={onViewValidation}
              isValidationFetching={
                validationLoadingCustomerId === customer.id
              }
            />
          );
        })}
      </Tbody>
    </Table>
  );
};

export default AccountManagement;
